import { useState } from 'react';
import { Button, Flex, Input, List, Popconfirm, Typography } from 'antd';
import { lineDiff, oneLineOk, shortDate, versionRow } from '../skillEditView';
import type { SkillDraft, SkillHistory } from '../types';
import SkillDiff from './SkillDiff';

/** Published versions (with rollback for publishers) and other people's open drafts. */
const SkillVersions = ({
  history,
  drafts,
  dirty,
  busy,
  liveText,
  copyWarning,
  onRollback,
  onCopy,
}: {
  history: SkillHistory;
  /** Other people's open drafts (the caller's own is in the editor). */
  drafts: SkillDraft[];
  dirty: boolean;
  busy: boolean;
  /** The live text, which View compares an open draft against. */
  liveText: string;
  /** Set when copying would replace the caller's own saved draft. */
  copyWarning: string | null;
  onRollback: (version: string, note: string) => void;
  onCopy: (draft: SkillDraft) => void;
}) => {
  const [note, setNote] = useState('');
  const [viewing, setViewing] = useState<Record<string, boolean>>({});
  return (
    <Flex vertical gap={8}>
      <List
        size="small"
        header={<Typography.Text strong>Versions</Typography.Text>}
        dataSource={history.published}
        renderItem={(v) => {
          const row = versionRow(v);
          return (
            <List.Item
              actions={
                history.canPublish && !v.live
                  ? [
                      <Popconfirm
                        key="live"
                        title="Make this version live?"
                        description={
                          <Input
                            value={note}
                            maxLength={200}
                            placeholder="Why? (required)"
                            aria-label="Reason for rolling back"
                            onChange={(e) => setNote(e.target.value)}
                          />
                        }
                        okText="Make live"
                        okButtonProps={{ disabled: !oneLineOk(note) }}
                        onConfirm={() => onRollback(v.version, note.trim())}
                        onOpenChange={(open) => open && setNote('')}
                      >
                        <Button size="small" disabled={busy || dirty}>
                          Make this live
                        </Button>
                      </Popconfirm>,
                    ]
                  : []
              }
            >
              <List.Item.Meta title={row.title} description={row.detail} />
            </List.Item>
          );
        }}
      />
      {drafts.length > 0 && (
        <List
          size="small"
          header={<Typography.Text strong>Open drafts</Typography.Text>}
          dataSource={drafts}
          renderItem={(d) => {
            const who = d.authorName ?? d.author;
            const copy = (
              <Button
                key="copy"
                size="small"
                disabled={dirty || busy}
                onClick={copyWarning ? undefined : () => onCopy(d)}
              >
                Copy into my draft
              </Button>
            );
            return (
              <List.Item
                actions={[
                  <Button
                    key="view"
                    size="small"
                    type="link"
                    aria-expanded={!!viewing[d.version]}
                    aria-label={`${viewing[d.version] ? 'Hide' : 'View'} ${who}'s draft`}
                    onClick={() =>
                      setViewing((v) => ({ ...v, [d.version]: !v[d.version] }))
                    }
                  >
                    {viewing[d.version] ? 'Hide' : 'View'}
                  </Button>,
                  copyWarning ? (
                    <Popconfirm
                      key="copy"
                      title="Replace your own draft?"
                      description={copyWarning}
                      okText="Replace my draft"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => onCopy(d)}
                      disabled={dirty || busy}
                    >
                      {copy}
                    </Popconfirm>
                  ) : (
                    copy
                  ),
                ]}
              >
                <Flex vertical style={{ width: '100%' }}>
                  <List.Item.Meta title={who} description={shortDate(d.at)} />
                  {viewing[d.version] && (
                    <>
                      <Typography.Text type="secondary">
                        Their draft, marked against the live version:
                      </Typography.Text>
                      <SkillDiff lines={lineDiff(liveText, d.text)} />
                    </>
                  )}
                </Flex>
              </List.Item>
            );
          }}
        />
      )}
    </Flex>
  );
};

export default SkillVersions;
